import { DuplicateExpenseCardFragment as ExpensableDuplicate } from 'generated/resources/graphql'
import { uniq } from 'lodash'

import { Session } from 'modules/core/hooks/useGetCurrentSession'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import type { Expensable as ExpensableDataTable } from 'modules/expenses/components/expenses/DataTable/types'
import { Expensable as ExpensableDetail } from 'modules/expenses/types/Expensable'

export const EXPENSABLE_CLASS = 'Expenses::Expensable'
export const EXPENSE_CLASS = 'Expenses::Expense'
export const MILEAGE_CLASS = 'Expenses::Mileage'

type Expensable = ExpensableDetail | ExpensableDataTable

export const expensableResource = <T extends Expensable>(
  expensable: T
): T['expense'] | T['mileage'] | T['perDiem'] =>
  expensable.expense ?? expensable.mileage ?? expensable.perDiem

export const expensableReimbursableCurrency = (expensable: Expensable) =>
  expensable?.reimbursableCurrency || expensable.currency

export const expensableCreationType = (expensable: Expensable | ExpensableDuplicate) =>
  expensable?.expense?.creationType ?? 'manual'

export const expensablePendingApproversNames = (expensable: Expensable) => {
  return uniq(expensable.currentApprovers.map((approver) => approver.fullName))
}

export const isOwnExpensable = (expensable: Expensable, currentEmployee: Session['employee']) =>
  expensable.owner?.id === currentEmployee?.id

export const isExpensableReporter = (
  expensable: Expensable,
  currentEmployee: Session['employee']
) => expensable.reporter?.id === currentEmployee?.id

export const isDraftExpensable = (expensable: Expensable) => expensable.status === 'draft'

export const isPendingExpensable = (expensable: Expensable | ExpensableDuplicate) =>
  expensable.status === 'pending'

export const isApprovedExpensable = (expensable: Expensable) => expensable.status === 'approved'

export const isRejectedExpensable = (expensable: Expensable) => expensable.status === 'rejected'

export const isChangesRequestedExpensable = (expensable: Expensable) =>
  expensable.status === 'changes_requested'

export const isPaidExpensable = (expensable: Expensable) => expensable.status === 'paid'

const isInPayrollExpensable = (expensable: Expensable) => expensable.status === 'in_payroll'

export const isManualExpensable = (expensable: Expensable) =>
  expensableCreationType(expensable) === 'manual'

export const isAutomaticExpensable = (expensable: Expensable | ExpensableDuplicate) =>
  expensableCreationType(expensable) === 'automatic'

export const isDeletableExpensable = (
  expensable: Expensable,
  canDelete: boolean,
  currentEmployee: Session['employee']
) => {
  const isDraftManual = isManualExpensable(expensable) && isDraftExpensable(expensable)
  const isAutomaticDraftWith0Amount =
    isAutomaticExpensable(expensable) &&
    isDraftExpensable(expensable) &&
    Number(expensable.amount) === 0
  const isOwnOrReporter =
    isOwnExpensable(expensable, currentEmployee) ||
    isExpensableReporter(expensable, currentEmployee)

  if ((isDraftManual || isAutomaticDraftWith0Amount) && isOwnOrReporter) return true

  return isRejectedExpensable(expensable) && canDelete
}

export const isReviewableExpensable = (expensable: Expensable | ExpensableDuplicate) =>
  isPendingExpensable(expensable) &&
  expensable.currentUserApprovalStatus === 'pending' &&
  !isCardPaymentPending(expensable)

export const isCardPaymentPending = (expensable: Expensable | ExpensableDuplicate) =>
  isAutomaticExpensable(expensable) && expensable.expense?.cardPayment?.status === 'pending'

export const isRestartableExpensable = (expensable: Expensable, canRestartApprovalFlow: boolean) =>
  canRestartApprovalFlow &&
  (isApprovedExpensable(expensable) ||
    isRejectedExpensable(expensable) ||
    isPendingExpensable(expensable))

export const isReimbursableExpensable = (expensable: Expensable) => {
  if (!isApprovedExpensable(expensable) || !isManualExpensable(expensable)) return false

  return expensable.type !== 'expense' || expensable.expense?.payment === 'reimbursable'
}

export const isImmutableExpensable = (expensable: Expensable) =>
  isApprovedExpensable(expensable) ||
  isPaidExpensable(expensable) ||
  isInPayrollExpensable(expensable) ||
  expensable.status === 'sent_to_pay'

export const isRemovableFromPayrollExpensable = (expensable: Expensable) =>
  isInPayrollExpensable(expensable) && (expensable.supplement?.id || expensable.compensation?.id)

export const everyReimbursableCurrencyAllowed = (expensables: Expensable[]) =>
  expensables.every((expensable) => {
    const currency = expensable.owner?.legalEntity?.currency || expensable.company.currency
    return expensableReimbursableCurrency(expensable) === currency
  })

export const showNotAllowedCurrenciesMessage = () =>
  app.setFlash({
    type: 'wrong',
    message: i18n.t('expenses_v2.payroll.not_allowed_currencies'),
  })

export const getLabelFromRate = (locale: string, currency: string, value: number, name: string) => {
  return `${new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(value)} - ${name}`
}
